import { Fragment, useCallback, useEffect, useId, useState } from 'react';
import type {
  OpenPayment, OpenPaymentFilters, OpenPaymentList, OpenPaymentReason, TripDetail, TripList, TripPayment, TripPaymentStatus, TripSort, TripSummary,
} from '../../../services/src/shared/api-types.ts';
import type { Api } from '../api.ts';
import type { ScreenProps } from '../App.tsx';
import { fiscalYearLabel, tripsCsv } from '../../../services/src/shared/trip-money.ts';
import { day, money, moneyWhole, num, plural, when } from '../format.ts';
import { Empty, ErrorBanner, Loading, Stat, useDebounced } from '../ui.tsx';

type View = 'trips' | 'unmatched';

const VIEWS: { view: View; label: string; hash: string }[] = [
  { view: 'trips', label: 'By trip', hash: '#/payments' },
  { view: 'unmatched', label: 'Unmatched', hash: '#/payments/unmatched' },
];

const viewFromHash = (): View => (location.hash.split('/')[2] === 'unmatched' ? 'unmatched' : 'trips');

/** "Dec 1" from 2025-12-01. */
const plainDay = (iso: string) => day(iso).replace(/ 0(\d)$/, ' $1');
/** "Mar 02 '27": trip dates run into next year. */
const dayYear = (iso: string) => `${day(iso)} '${iso.slice(2, 4)}`;
/** "December 2025" from 2025-12. */
const monthName = (ym: string) => new Date(`${ym}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const REASON_ORDER: OpenPaymentReason[] = ['not-connected', 'no-charge', 'check', 'wire', 'not-card', 'waiting', 'before-fy'];

function reasonLabel(r: OpenPaymentReason, fiscalYearStart: string): string {
  switch (r) {
    case 'before-fy':
      return `Trip before ${plainDay(fiscalYearStart)}`;
    case 'check':
      return 'Check not cleared';
    case 'wire':
      return 'Wire not cleared';
    case 'not-connected':
      return 'Card not connected';
    case 'not-card':
      return 'Bill pay or bank, not matched';
    case 'waiting':
      return 'Charge not in yet';
    case 'no-charge':
      return 'No charge found';
  }
}

const STATUS_LABEL: Record<Exclude<TripPaymentStatus, 'unmatched'>, string> = {
  reconciled: 'Reconciled in Xero',
  unreconciled: 'In Xero, not reconciled',
  review: 'In review',
  matched: 'Matched, waiting to post',
  failed: 'Posting failed',
};

export function Payments({ api }: ScreenProps) {
  const [view, setView] = useState<View>(viewFromHash);
  const [counts, setCounts] = useState<Partial<Record<View, number>>>({});
  const panel = useId();

  // Home links straight to a view; follow the hash while this screen is open.
  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // The other view's count, once, so both tabs show a number.
  useEffect(() => {
    let live = true;
    const other: View = viewFromHash() === 'trips' ? 'unmatched' : 'trips';
    const load = other === 'trips' ? api.trips({ fy: currentFy() }).then((r) => r.total) : api.openPayments({}).then((r) => r.total);
    load.then((n) => live && setCounts((c) => ({ [other]: n, ...c })), () => undefined);
    return () => {
      live = false;
    };
  }, [api]);
  // Stable callbacks, and no new state for an unchanged count: either would refetch the list in a loop.
  const onCount = useCallback((v: View, n: number) => setCounts((c) => (c[v] === n ? c : { ...c, [v]: n })), []);
  const onTrips = useCallback((n: number) => onCount('trips', n), [onCount]);
  const onUnmatched = useCallback((n: number) => onCount('unmatched', n), [onCount]);

  const pick = (v: View) => {
    setView(v);
    history.replaceState(null, '', VIEWS.find((x) => x.view === v)!.hash);
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Salesforce payments</h1>
          <p className="lede">
            {view === 'trips'
              ? 'Profit by trip from Salesforce, and whether each payment is reconciled in Xero.'
              : 'Salesforce payments with no bank charge matched or in review.'}
          </p>
        </div>
      </header>

      <div role="tablist" aria-label="View" className="tabs">
        {VIEWS.map((t) => (
          <button
            key={t.view}
            type="button"
            role="tab"
            id={`${panel}-${t.view}`}
            aria-selected={view === t.view}
            aria-controls={panel}
            className="tab"
            onClick={() => pick(t.view)}
          >
            {t.label}
            {counts[t.view] !== undefined && <span className="mono">{num(counts[t.view]!)}</span>}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={panel} aria-labelledby={`${panel}-${view}`} className="panel-stack">
        {view === 'trips' ? <Trips api={api} onCount={onTrips} /> : <UnmatchedPayments api={api} onCount={onUnmatched} />}
      </div>
    </div>
  );
}

// ---------- by trip ----------

const open = (t: TripSummary) => t.payments - t.counts.reconciled;
const DEFAULT_FY_START = '2025-12-01';
/** The fiscal year today is in ("FY26"), the trip view's default. */
const currentFy = () => fiscalYearLabel(new Date().toISOString().slice(0, 10), DEFAULT_FY_START);
/** "28.9%", "−4.2%". */
const pct = (m: number) => `${m < 0 ? '−' : ''}${Math.abs(m * 100).toFixed(1)}%`;
const SORTS: { sort: TripSort; label: string }[] = [
  { sort: 'date', label: 'Trip date' },
  { sort: 'projectedProfit', label: 'Projected profit' },
  { sort: 'projectedMargin', label: 'Projected margin' },
  { sort: 'profitToday', label: 'Profit today' },
];

function downloadCsv(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function Trips({ api, onCount }: { api: Api; onCount: (n: number) => void }) {
  const [search, setSearch] = useState('');
  const query = useDebounced(search);
  const [defaultFy] = useState(currentFy);
  const [fy, setFy] = useState(defaultFy);
  const [month, setMonth] = useState('');
  const [sort, setSort] = useState<TripSort>('date');
  const [list, setList] = useState<TripList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const ids = { search: useId(), fy: useId(), month: useId(), sort: useId() };

  const load = useCallback(() => {
    let live = true;
    setLoading(true);
    setError(null);
    api.trips({ fy, month: month || undefined, q: query || undefined, sort: sort === 'date' ? undefined : sort }).then(
      (r) => live && (setList(r), setLoading(false), fy === defaultFy && !month && !query && onCount(r.total)),
      (e: Error) => live && (setError(e.message), setLoading(false)),
    );
    return () => {
      live = false;
    };
  }, [api, fy, defaultFy, month, query, sort, onCount]);
  useEffect(load, [load]);

  const toggle = (id: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const items = list?.items ?? [];
  const totals = list?.totals;
  const fyStart = list?.fiscalYearStart ?? DEFAULT_FY_START;
  // The current year is offered even before it has trips.
  const years = [...new Map([[defaultFy, 0], ...(list?.fiscalYears ?? []).map((y) => [y.fy, y.trips] as const)])].sort((x, y) => x[0].localeCompare(y[0]));
  const scope = [fy === 'all' ? 'All years' : fy, month && monthName(month)].filter(Boolean).join(', ');

  const exportCsv = () => downloadCsv(`trips-${fy === 'all' ? 'all-years' : fy}${month ? `-${month}` : ''}.csv`, tripsCsv(items));

  return (
    <>
      {list && totals && (
        <div className="stats tight trip-stats">
          <Stat label="Trips" value={num(list.total)} note={scope} />
          <Stat
            label="Projected profit"
            value={totals.projected.trips ? signedWhole(totals.projected.profit) : '—'}
            warn={totals.projected.profit < 0}
            note={totals.projected.trips ? `${totals.projected.margin === null ? '' : `${pct(totals.projected.margin)} on `}${moneyWhole(totals.projected.income)}` : 'No trip prices yet'}
          />
          <Stat
            label="Profit today"
            value={totals.today.trips ? signedWhole(totals.today.profit) : '—'}
            warn={totals.today.profit < 0}
            note={
              totals.today.trips
                ? `${totals.today.margin === null ? '' : `${pct(totals.today.margin)} · `}${plural(totals.today.trips, 'trip')} with payments received`
                : 'No payments received yet'
            }
          />
          <Stat label="Paid" value={moneyWhole(totals.paid)} note={`of ${moneyWhole(totals.projectedCost)} projected`} />
          <Stat label="Open payments" value={num(totals.open)} note="Not reconciled in Xero" />
        </div>
      )}

      <div className="filters trip-filters">
        <label className="field grow" htmlFor={ids.search}>
          Search
          <input id={ids.search} type="search" placeholder="Trip name" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <label className="field fy" htmlFor={ids.fy}>
          Fiscal year
          <select className="select" id={ids.fy} value={fy} onChange={(e) => (setFy(e.target.value), setMonth(''))}>
            {years.map(([y, n]) => (
              <option key={y} value={y}>
                {y} ({num(n)})
              </option>
            ))}
            <option value="all">All years</option>
          </select>
        </label>
        <label className="field account" htmlFor={ids.month}>
          Trip month
          <select className="select" id={ids.month} value={month} onChange={(e) => setMonth(e.target.value)}>
            <option value="">All months</option>
            {(list?.months ?? []).map((m) => (
              <option key={m.month} value={m.month}>
                {monthName(m.month)} ({num(m.trips)})
              </option>
            ))}
          </select>
        </label>
        <label className="field age" htmlFor={ids.sort}>
          Sort by
          <select className="select" id={ids.sort} value={sort} onChange={(e) => setSort(e.target.value as TripSort)}>
            {SORTS.map((o) => (
              <option key={o.sort} value={o.sort}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn" onClick={exportCsv} disabled={!items.length}>
          Export CSV
        </button>
      </div>

      <div aria-busy={loading} className="panel-stack">
        {list?.asOf && <p className="small muted">As of {when(list.asOf)}. Click a trip to see every payment on it.</p>}
        {error && <ErrorBanner message={error} onRetry={load} />}
        {!list && !error && <Loading kind="table" />}
        {list && items.length === 0 && !error && (
          <Empty>{list.asOf ? 'No trips for these filters.' : 'No trips yet. They fill in after the next sync with Salesforce.'}</Empty>
        )}
        {items.length > 0 && (
          <div className="card table-card">
            <table className="table trips">
              <thead>
                <tr>
                  <th scope="col">Trip date</th>
                  <th scope="col">Trip</th>
                  <th scope="col" className="num">Trip price</th>
                  <th scope="col" className="num">Projected cost</th>
                  <th scope="col" className="num">Projected profit</th>
                  <th scope="col" className="num">Received</th>
                  <th scope="col" className="num">Paid</th>
                  <th scope="col" className="num">Profit today</th>
                  <th scope="col" className="xero-col">Reconciled</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <TripRow key={t.tripId} api={api} trip={t} expanded={expanded.has(t.tripId)} onToggle={() => toggle(t.tripId)} fiscalYearStart={fyStart} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list && items.length < list.total && (
          <p className="small muted table-foot">
            Showing {num(items.length)} of {num(list.total)}. Search or pick a month to narrow it down.
          </p>
        )}
        {items.length > 0 && (
          <p className="small muted table-foot">Projected: trip price less contracted vendor cost (or paid, if higher). Today: money received less Salesforce payments.</p>
        )}
      </div>
    </>
  );
}

/** Whole dollars with a minus sign for a loss. */
const signedWhole = (n: number) => (n < 0 ? `−${moneyWhole(-n)}` : moneyWhole(n));
const NONE = <span className="muted">—</span>;
/** Whole dollars in the trip table; the expanded payments and the CSV keep cents. */
const orNone = (n: number | null) => (n === null ? NONE : moneyWhole(n));

/** Profit with its margin under it, in the loss color below zero. */
function ProfitCell({ profit, margin }: { profit: number | null; margin: number | null }) {
  if (profit === null) return <td className="num">{NONE}</td>;
  return (
    <td className={profit < 0 ? 'num loss' : 'num'}>
      {signedWhole(profit)}
      {margin !== null && <span className="sub">{pct(margin)}</span>}
    </td>
  );
}

function TripRow({ api, trip, expanded, onToggle, fiscalYearStart }: { api: Api; trip: TripSummary; expanded: boolean; onToggle: () => void; fiscalYearStart: string }) {
  const detailId = useId();
  const n = open(trip);
  const done = trip.counts.reconciled;
  return (
    <Fragment>
      <tr className={expanded ? 'trip-row expanded' : 'trip-row'} onClick={onToggle}>
        <td className="date">{trip.date ? dayYear(trip.date) : <span className="muted">None</span>}</td>
        <td className="trip-name">
          <button type="button" className="trip-toggle" aria-expanded={expanded} aria-controls={detailId} onClick={(e) => (e.stopPropagation(), onToggle())}>
            <svg className="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
            <span className="what">
              <span>{trip.name}</span>
              <span className="sub">
                {plural(trip.payments, 'payment')}
                {n === 0 ? <span className="chip ok">All reconciled</span> : <span className="chip open">{num(n)} open</span>}
              </span>
            </span>
          </button>
        </td>
        <td className="num">{orNone(trip.projectedIncome)}</td>
        <td className="num">{moneyWhole(trip.projectedCost)}</td>
        <ProfitCell profit={trip.projectedProfit} margin={trip.projectedMargin} />
        <td className="num">{orNone(trip.received)}</td>
        <td className="num">{moneyWhole(trip.paid)}</td>
        <ProfitCell profit={trip.profitToday} margin={trip.marginToday} />
        <td className="xero-col">
          <span className="xero-frac">
            <span className="mono">
              {num(done)} of {num(trip.payments)}
            </span>
            <span className="bar" aria-hidden="true">
              <span style={{ width: `${trip.payments ? (done / trip.payments) * 100 : 0}%` }} />
            </span>
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="trip-detail" id={detailId}>
          <td colSpan={9}>
            <TripPayments api={api} tripId={trip.tripId} fiscalYearStart={fiscalYearStart} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function TripPayments({ api, tripId, fiscalYearStart }: { api: Api; tripId: string; fiscalYearStart: string }) {
  const [detail, setDetail] = useState<TripDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    let live = true;
    setError(null);
    api.trip(tripId).then(
      (d) => live && setDetail(d),
      (e: Error) => live && setError(e.message),
    );
    return () => {
      live = false;
    };
  }, [api, tripId]);
  useEffect(load, [load]);

  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!detail) return <p className="small muted trip-loading" role="status">Loading payments…</p>;
  const t = detail.trip;
  const c = t.counts;
  const statuses = [
    [c.reconciled, 'reconciled'],
    [c.unreconciled, 'in Xero, not reconciled'],
    [c.review, 'in review'],
    [c.matched, 'matched, waiting to post'],
    [c.failed, 'failed to post'],
    [c.unmatched, 'unmatched'],
  ] as const;
  return (
    <div className="trip-payments">
      <div className="trip-facts small">
        {statuses.map(([n, label]) => (
          <span key={label} className={n === 0 ? 'muted' : label === 'reconciled' ? '' : 'st-warn'}>
            <strong className="mono">{num(n)}</strong> {label}
          </span>
        ))}
      </div>
      <div className="trip-facts small muted">
        <span>Contracted {t.contracted === null ? 'none in Salesforce' : money(t.contracted)}</span>
        <span>Paid {money(t.paid)}</span>
        {t.contracted !== null && t.paid > t.contracted && <span className="st-warn">{money(t.paid - t.contracted)} paid over contract</span>}
        {t.contracted !== null && t.paid < t.contracted && <span>{money(t.contracted - t.paid)} left to pay</span>}
        <span>In Xero {money(t.inXero)}</span>
        <span>Reconciled {money(t.reconciledAmount)}</span>
      </div>
      <table className="table nested">
        <thead>
          <tr>
            <th scope="col">Paid</th>
            <th scope="col">Payment</th>
            <th scope="col">Method</th>
            <th scope="col">Status</th>
            <th scope="col" className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {detail.payments.map((p) => (
            <tr key={p.id}>
              <td className="date">{dayYear(p.date)}</td>
              <td>
                {p.vendor && `${p.vendor} · `}
                <span className="nowrap">{p.name}</span>
              </td>
              <td className="acct">{p.method || <span className="muted">None</span>}</td>
              <td>
                <StatusChip p={p} fiscalYearStart={fiscalYearStart} />
              </td>
              <td className="num">{money(p.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusChip({ p, fiscalYearStart }: { p: TripPayment; fiscalYearStart: string }) {
  if (p.status === 'unmatched') return <span className="chip">{p.reason ? reasonLabel(p.reason, fiscalYearStart) : 'Unmatched'}</span>;
  const tone = p.status === 'reconciled' ? ' ok' : p.status === 'unreconciled' || p.status === 'failed' ? ' open' : '';
  return <span className={`chip${tone}`}>{STATUS_LABEL[p.status]}</span>;
}

// ---------- unmatched ----------

function UnmatchedPayments({ api, onCount }: { api: Api; onCount: (n: number) => void }) {
  const [search, setSearch] = useState('');
  const query = useDebounced(search);
  const [reason, setReason] = useState<OpenPaymentReason | ''>('');
  const [sort, setSort] = useState<'date' | 'tripDate'>('date');
  const [list, setList] = useState<OpenPaymentList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const ids = { search: useId(), reason: useId(), sort: useId() };

  const load = useCallback(() => {
    const filters: OpenPaymentFilters = { reason: reason || undefined, sort, q: query || undefined };
    let live = true;
    setLoading(true);
    setError(null);
    api.openPayments(filters).then(
      (r) => live && (setList(r), setLoading(false), !reason && !query && onCount(r.total)),
      (e: Error) => live && (setError(e.message), setLoading(false)),
    );
    return () => {
      live = false;
    };
  }, [api, reason, sort, query, onCount]);
  useEffect(load, [load]);

  const fy = list?.fiscalYearStart ?? '2025-12-01';
  const allCount = list ? Object.values(list.reasons).reduce((s, n) => s + (n ?? 0), 0) : 0;

  return (
    <>
      {list && (
        <div className="stats tight">
          <Stat label="Payments" value={num(list.total)} note={reason ? reasonLabel(reason, fy) : 'All reasons'} />
          <Stat label="Amount" value={money(list.amount)} />
          <Stat label="Card not connected" value={num(list.reasons['not-connected'] ?? 0)} />
          <Stat label="No charge found" value={num(list.reasons['no-charge'] ?? 0)} />
        </div>
      )}

      <div className="filters">
        <label className="field grow" htmlFor={ids.search}>
          Search
          <input id={ids.search} type="search" placeholder="Vendor, trip, PMT number, card or amount" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <label className="field account" htmlFor={ids.reason}>
          Why
          <select className="select" id={ids.reason} value={reason} onChange={(e) => setReason(e.target.value as OpenPaymentReason | '')}>
            <option value="">All reasons{list ? ` (${num(allCount)})` : ''}</option>
            {REASON_ORDER.map((r) => (
              <option key={r} value={r}>
                {reasonLabel(r, fy)}
                {list ? ` (${num(list.reasons[r] ?? 0)})` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="field age" htmlFor={ids.sort}>
          Sort by
          <select className="select" id={ids.sort} value={sort} onChange={(e) => setSort(e.target.value as 'date' | 'tripDate')}>
            <option value="date">Payment date</option>
            <option value="tripDate">Trip date</option>
          </select>
        </label>
      </div>

      <div aria-busy={loading} className="panel-stack">
        {list?.asOf && <p className="small muted">As of {when(list.asOf)}.</p>}
        {error && <ErrorBanner message={error} onRetry={load} />}
        {!list && !error && <Loading kind="table" />}
        {list && list.items.length === 0 && !error && (
          <Empty>{list.asOf ? 'No unmatched payments for these filters.' : 'No matching run yet. The list fills in after the next sync with Salesforce.'}</Empty>
        )}
        {list && list.items.length > 0 && (
          <div className="card table-card">
            <table className="table min-720">
              <thead>
                <tr>
                  <th scope="col">Paid</th>
                  <th scope="col">Payment</th>
                  <th scope="col">Trip date</th>
                  <th scope="col">Method</th>
                  <th scope="col">Why</th>
                  <th scope="col" className="num">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((p) => (
                  <PaymentRow key={p.id} p={p} fiscalYearStart={fy} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list && list.items.length < list.total && (
          <p className="small muted table-foot">
            Showing {num(list.items.length)} of {num(list.total)}. Search or pick a reason to narrow it down.
          </p>
        )}
      </div>
    </>
  );
}

function PaymentRow({ p, fiscalYearStart }: { p: OpenPayment; fiscalYearStart: string }) {
  return (
    <tr>
      <td className="date">{dayYear(p.date)}</td>
      <td>
        <div className="what">
          <span>{[p.vendor, p.name].filter(Boolean).join(' · ')}</span>
          {p.trip && <span className="sub">{p.trip}</span>}
        </div>
      </td>
      <td className="date">{p.tripDate ? dayYear(p.tripDate) : <span className="muted">None</span>}</td>
      <td className="acct">{p.method || <span className="muted">None</span>}</td>
      <td>
        <span className="chip">{reasonLabel(p.reason, fiscalYearStart)}</span>
        {p.xero && <span className="chip xero">{p.xero === 'unreconciled' ? 'In Xero, not reconciled' : 'In Xero'}</span>}
      </td>
      <td className="num">{money(p.amount)}</td>
    </tr>
  );
}
